'use client';

import Link from 'next/link';
import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';
import { ParsedUrlQuery } from 'querystring';

const toPageQuery = (searchParams: URLSearchParams, page: number): ParsedUrlQuery => {
  const copy = new URLSearchParams(searchParams);
  copy.set('page', page.toString());
  const result: Record<string, string | undefined> = Object.fromEntries(copy.entries());
  if (page === 1) {
    result.page = undefined;
  }
  return result;
};

const MAX_N_OF_PAGES = 6;
const PAGES_BEFORE_CURRENT = 1;

export const Pagination = ({
  currentPage,
  numberOfPages: nOfPages,
  disabled,
}: {
  currentPage: number;
  numberOfPages: number;
  disabled: boolean;
}) => {
  const hasPrevPage = currentPage > 1;
  const hasNextPage = currentPage < nOfPages;
  const searchParams = useSearchParams();

  let from = Math.max(currentPage - PAGES_BEFORE_CURRENT, 1);
  const to = Math.min(nOfPages, from + MAX_N_OF_PAGES - PAGES_BEFORE_CURRENT);
  if (to < from + MAX_N_OF_PAGES - PAGES_BEFORE_CURRENT) {
    from = Math.max(1, to - MAX_N_OF_PAGES + PAGES_BEFORE_CURRENT);
  }
  const pageNumbers: Array<number | '...'> = Array.from(new Array(to - from + 1), (_, i) => i + from);
  if (Math.min(nOfPages, currentPage + 4) < nOfPages) {
    pageNumbers[3] = '...';
    pageNumbers[4] = nOfPages - 1;
    pageNumbers[5] = nOfPages;
  }

  return (
    <nav className="flex flex-row items-center list-none justify-center mt-28">
      {hasPrevPage && (
        <li>
          <Link title={`pagina precedente (${currentPage - 1})`} href={{ query: toPageQuery(searchParams, currentPage - 1) }}>
            <svg
              className={clsx('transition-all hover:-translate-x-1 hover:fill-elty-green', disabled ? 'fill-gray-200' : 'fill-inherit')}
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.7197 16.5303C11.4268 16.2374 11.4268 15.7626 11.7197 15.4697L19.2197 7.96967C19.5126 7.67678 19.9874 7.67678 20.2803 7.96967C20.5732 8.26256 20.5732 8.73744 20.2803 9.03033L13.3107 16L20.2803 22.9697C20.5732 23.2626 20.5732 23.7374 20.2803 24.0303C19.9874 24.3232 19.5126 24.3232 19.2197 24.0303L11.7197 16.5303Z"
              />
            </svg>
          </Link>
        </li>
      )}
      {pageNumbers.map(pageNumber =>
        pageNumber === '...' ? (
          <li
            key={pageNumber}
            aria-hidden
            className={clsx('h-8 w-8 flex items-center justify-center transition-colors', disabled && 'text-gray-200')}
          >
            ...
          </li>
        ) : (
          <li key={pageNumber}>
            <Link
              className={clsx(
                'h-8 w-8 flex items-center justify-center rounded transition-colors ',
                pageNumber === currentPage ? 'bg-elty-green text-white' : 'hover:text-elty-green',
                disabled && 'text-gray-200',
              )}
              href={{ query: toPageQuery(searchParams, pageNumber) }}
            >
              {pageNumber}
            </Link>
          </li>
        ),
      )}
      {hasNextPage && (
        <li>
          <Link title={`pagina successiva (${currentPage + 1})`} href={{ query: toPageQuery(searchParams, currentPage + 1) }}>
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={clsx(
                'rotate-180 transition-all hover:fill-elty-green hover:translate-x-1',
                disabled ? 'fill-gray-200' : 'fill-inherit',
              )}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.7197 16.5303C11.4268 16.2374 11.4268 15.7626 11.7197 15.4697L19.2197 7.96967C19.5126 7.67678 19.9874 7.67678 20.2803 7.96967C20.5732 8.26256 20.5732 8.73744 20.2803 9.03033L13.3107 16L20.2803 22.9697C20.5732 23.2626 20.5732 23.7374 20.2803 24.0303C19.9874 24.3232 19.5126 24.3232 19.2197 24.0303L11.7197 16.5303Z"
              />
            </svg>
          </Link>
        </li>
      )}
    </nav>
  );
};
